import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AdminAccessDirective, HasPermissionDirective, MemberAccessDirective } from '../../shared/directives/permission.directive';
import { PreventClickDirective } from '../../shared/directives/prevent-click.directive';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AsyncPipe, NgIf } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { SimpleOrganization } from '../../../../services/organization/organization.service';
import { PropertyManagementCompany } from '../../../../models/entities/property-management-company';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, delay } from 'rxjs/operators';
import { PropertyManagementCompanyDataService } from '../../../../services/property-management-company/property-manager-company-data.service';
import { OrganizationDataService } from '../../../../services/organization/organization-data.service';
import { AuthService } from '../../../../services/auth/auth.service';
import { IntercomService } from '../../../../services/intercom.service';
import { GrowlerService } from '../../../../services/growler.service';
import { AvatarBlockModule } from '../../shared/avatar-block/avatar-block.module';
import { HostListenerService } from '../../../../services/host-listener.service';

@UntilDestroy()
@Component({
    selector: 'app-header-menu-items',
    templateUrl: './header-menu-items.component.html',
    styleUrls: ['./header-menu-items.component.scss'],
    standalone: true,
    imports: [
        MatMenuModule,
        RouterLink,
        MatIconModule,
        MemberAccessDirective,
        AdminAccessDirective,
        HasPermissionDirective,
        PreventClickDirective,
        MatFormFieldModule,
        MatInputModule,
        NgIf,
        MatProgressSpinnerModule,
        ReactiveFormsModule,
        MatListModule,
        AsyncPipe,
        CdkVirtualScrollViewport,
        CdkFixedSizeVirtualScroll,
        CdkVirtualForOf,
        AvatarBlockModule,
    ],
})
export class HeaderMenuItemsComponent implements OnInit
{
    @Output() notificationsMenuItemClicked = new EventEmitter<void>();

    organizationSearch = this._fb.control<string>({value: '', disabled: true});
    organizations: SimpleOrganization[] = [];
    isSearchingOrganizations = false;

    readonly itemHeight = 64;
    readonly viewportHeight = 200;

    constructor(
            private _fb: FormBuilder,
            private _propertyManagementCompanyData: PropertyManagementCompanyDataService,
            private _organizationDataService: OrganizationDataService,
            private _authService: AuthService,
            private _host: HostListenerService,
            private _intercom: IntercomService,
            private _router: Router,
            private _growler: GrowlerService,
    ) {
    }

    get pmcId() {
        return this._propertyManagementCompanyData.pmcId;
    }

    get pmc$() {
        return this._propertyManagementCompanyData.pmc$;
    }

    get mobileMode$() {
        return this._host.isMobileMode();
    }

    get filteredResults() {
        return this.organizations.filter((organization) => organization.visible);
    }

    set filterTerm(term: string) {
        term = term ? term.trim().toLowerCase() : '';
        for (const organization of this.organizations) {
            if (term !== '') {
                let value = organization.name.trim().toLowerCase();
                value += organization.id;
                organization.visible = value.indexOf(term) > -1;
            } else {
                organization.visible = true;
            }
        }
    }

    ngOnInit() {
        this._organizationDataService.orgList$.pipe(
                delay(120),
                untilDestroyed(this),
        ).subscribe({
            next: (organizations) => {
                this.filterTerm = '';
                this.organizations = organizations;
                this.organizationSearch.enable();
            },
        });

        this.organizationSearch.valueChanges.pipe(
                debounceTime(500),
                untilDestroyed(this),
        ).subscribe({
            next: (term) => this.filterTerm = term,
        });
    }

    sendIntercomMessage() {
        this._intercom.newMessage();
    }

    logout() {
        this._authService.logout();
    }

    selectOrganization(change: MatSelectionListChange) {
        const selection: SimpleOrganization | PropertyManagementCompany = change.source.selectedOptions.selected[0].value;
        if (!selection.hasOwnProperty('organizationsCount')) {
            this._organizationDataService.switchOrganization(selection.id).pipe(untilDestroyed(this)).subscribe({
                next: ([url, isExternalRedirect]: [string, boolean]) => {
                    if (isExternalRedirect) {
                        window.location.href = url;
                    } else {
                        return this._router.navigateByUrl(url, {skipLocationChange: true});
                    }
                },
                error: () => this._growler.oops('There was a problem switching current organization.'),
            });
        }
    }
}
